import React, { useEffect, useState } from 'react'
import { Button, theme } from '@acorns/grove'
import { useRemoteStrings, useAnalyticsClient } from '@acorns/web-utils'
import { TestIDs } from 'test/utils/testIds'
import { PromoCodesDrawerInputForm, StyledDrawer, StyledTextInput } from '../styled-components'
import { DrawerBanner } from './DrawerBanner'
import { useReferralCode } from './useReferralCode'
import { PromoCodeValidationSubText } from './PromoCodeValidationSubText'
import { Spinner } from '@acorns/web-components'
import { ValidationState } from './types'
import {
  RegistrationPromoCodeDrawerViewedType,
  trackRegistrationPromoCodeDrawerViewedPayload,
  trackRegistrationPromoCodeSkipCtaTappedPayload,
  trackRegistrationPromoCodeNextCtaTappedPayload,
} from 'generated/trackingEvents'
import { translateEventProps } from 'utils/analytics/eventSpec'
import { PromoCodesProps } from './usePromoCodes'

type Props = Pick<
  PromoCodesProps,
  | 'appliedPromoCode'
  | 'description'
  | 'isDrawerOpen'
  | 'isErrorSubTextShown'
  | 'isPromoCodeVerificationLoading'
  | 'lastValidPromoCode'
  | 'onCloseDrawer'
  | 'termsUrl'
  | 'validatePromoCode'
  | 'validationState'
>

export const PromoCodesDrawer = ({
  appliedPromoCode,
  description,
  isDrawerOpen,
  isErrorSubTextShown,
  isPromoCodeVerificationLoading,
  lastValidPromoCode,
  onCloseDrawer,
  termsUrl,
  validatePromoCode,
  validationState,
}: Props) => {
  const [isSubtextActive, setIsSubtextActive] = useState(true)
  const [promoCodeInput, setPromoCodeInput] = useState('')
  const { hasReferralCode } = useReferralCode()
  const { getRemoteString } = useRemoteStrings()
  const analytics = useAnalyticsClient()

  const isValidSubTextShown =
    validationState === ValidationState.USER_INPUT_VALID ||
    validationState === ValidationState.QUERY_PARAM_VALID
  const isSubTextShown = isSubtextActive && (isErrorSubTextShown || isValidSubTextShown)

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPromoCodeInput(event.currentTarget.value)
    setIsSubtextActive(false)
  }

  const handleApply = () => {
    validatePromoCode({
      variables: {
        name: promoCodeInput,
      },
    })

    const [eventName, payLoad] = trackRegistrationPromoCodeNextCtaTappedPayload({
      promotion: promoCodeInput,
    })
    analytics.track(eventName, translateEventProps(payLoad))
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()

    handleApply()
  }

  const handleClose = () => {
    onCloseDrawer()

    const [eventName, payLoad] = trackRegistrationPromoCodeSkipCtaTappedPayload()
    analytics.track(eventName, translateEventProps(payLoad))
  }

  const defaultPromoCode = getRemoteString({
    key: 'create_account.promo_code.default.name',
  })

  useEffect(() => {
    if (!isDrawerOpen) return

    const [eventName, payLoad] = trackRegistrationPromoCodeDrawerViewedPayload({
      promotion: appliedPromoCode,
      type: appliedPromoCode
        ? RegistrationPromoCodeDrawerViewedType.Edit_code
        : RegistrationPromoCodeDrawerViewedType.Enter_new_code,
    })

    analytics.track(eventName, translateEventProps(payLoad))
  }, [isDrawerOpen])

  useEffect(() => {
    if (
      validationState === ValidationState.USER_INPUT_VALID ||
      validationState === ValidationState.QUERY_PARAM_VALID
    ) {
      setPromoCodeInput(appliedPromoCode)
    }

    if (validationState === ValidationState.QUERY_PARAM_INVALID) {
      setPromoCodeInput('')
    }
  }, [validationState, appliedPromoCode, setPromoCodeInput, defaultPromoCode])

  useEffect(() => {
    if (!isPromoCodeVerificationLoading) {
      setIsSubtextActive(true)
    }
  }, [isPromoCodeVerificationLoading, setIsSubtextActive])

  if (hasReferralCode) {
    return null
  }

  const hideBanner =
    validationState === ValidationState.QUERY_PARAM_USER_INPUT_INVALID ||
    validationState === ValidationState.QUERY_PARAM_INVALID ||
    lastValidPromoCode === ''

  return (
    <StyledDrawer
      bannerColor={theme.colors.naturalDarkOak}
      bannerMessage={
        <DrawerBanner
          appliedPromoCode={appliedPromoCode}
          description={description}
          lastValidPromoCode={lastValidPromoCode}
          termsUrl={termsUrl}
        />
      }
      hasBanner={!hideBanner}
      isOpen={isDrawerOpen}
      onClose={handleClose}
      mobileBannerMarginBottom={'160px'}
      title={getRemoteString({
        key: 'create_account.promo_code_drawer.title',
      })}
    >
      <PromoCodesDrawerInputForm
        data-testid={TestIDs.PROMO_CODES.PROMO_CODE_DRAWER_FORM}
        onSubmit={handleSubmit}
      >
        <StyledTextInput
          label={getRemoteString({ key: 'create_account.promo_code_drawer.input_field.label' })}
          placeholder={getRemoteString({
            key: 'create_account.promo_code_drawer.input_field.placeholder',
          })}
          onChange={handleChange}
          value={promoCodeInput}
          error={!isValidSubTextShown && isSubTextShown}
          testId={TestIDs.PROMO_CODES.PROMO_CODE_DRAWER_INPUT}
          enterKeyHint="go"
        />
        {isSubTextShown && <PromoCodeValidationSubText isValidSubTextShown={isValidSubTextShown} />}
      </PromoCodesDrawerInputForm>
      <Button
        disabled={promoCodeInput === '' || isSubtextActive || isPromoCodeVerificationLoading}
        onClick={handleApply}
      >
        {isPromoCodeVerificationLoading ? <Spinner /> : getRemoteString({ key: 'global.apply' })}
      </Button>
    </StyledDrawer>
  )
}
